import { useTranslations } from 'next-intl';
import { type FC, useEffect, useState } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { UserAvatar } from '@/app/components/shared/UserAvatar';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent } from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { HOME_PAGE } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import { reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { DeleteUserDialog } from './DeleteUserDialog';
import UserBioInput from './UserBioInput';
import UserNameInput from './UserNameInput';

interface UserProfileProps {
  open: boolean;
  onClose: () => void;
  source?: 'head' | 'user_name' | 'introduction' | 'background' | 'account_setting' | 'other';
}

export const UserProfile: FC<UserProfileProps> = ({ open, source, onClose }) => {
  const router = useRouter();
  const [dialogOpen, setDialogOpen] = useState(false);
  const t = useTranslations();
  const { userInfo, isTeamSpace } = useStore((state) => ({
    userInfo: state.userInfo,
    isTeamSpace: state.isTeamSpace,
  }));

  useEffect(() => {
    if (open) {
      reportEvent('page_view', {
        page_name: 'account_settings_page',
        source: source || 'other',
      });
    }
  }, [open, source]);

  const RenderSectionTitle = (title: string) => (
    <div className="mb-6 font-medium text-xl lg:mb-4 lg:text-lg">{title}</div>
  );
  const RenderDivider = () => <div className="my-8 border-system-white12 border-t lg:my-6" />;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="py-0 text-system-white" variant="fullScreen" closable={false}>
        <div className="relative mx-auto w-[556px] py-[80px] sm:w-auto lg:py-[68px]">
          <BaseLineClose
            className="sm:-right-1 absolute top-8 left-[600px] h-8 w-8 cursor-pointer sm:left-auto lg:h-6 lg:w-6"
            onClick={onClose}
          />
          <div className="mb-[48px] font-bold text-2xl lg:mb-8 lg:text-xl">{t('personal.accountSetting')}</div>

          {RenderSectionTitle(t('personal.basicInformation'))}

          <div className="mb-6">
            <Label className="mb-4 inline-block font-medium text-[18px] lg:mb-3 lg:text-sm">
              {t('personal.avatar')}
            </Label>
            <div className="relative block">
              {userInfo && <UserAvatar userInfo={userInfo} className="size-20 md:size-12" editable={true} />}
            </div>
          </div>

          <div className="mb-6">
            <UserNameInput />
          </div>

          <div className="mb-6">
            <UserBioInput />
          </div>

          {!isTeamSpace && (
            <>
              {RenderDivider()}

              {RenderSectionTitle(t('personal.accountInformation'))}

              {userInfo?.email && (
                <div className="mb-6">
                  <Label className="mb-4 inline-block font-medium text-base lg:mb-3 lg:text-sm">
                    {t('personal.email.label')}
                  </Label>
                  <Input value={userInfo.email} disabled={true} />
                  <div className="mt-2 text-system-white48 text-xs">{t('personal.email.note')}</div>
                </div>
              )}
              {userInfo?.phone && (
                <div className="mb-6">
                  <Label className="mb-4 inline-block font-medium text-base lg:mb-3 lg:text-sm">
                    {t('personal.phone.label')}
                  </Label>
                  <Input value={userInfo.phone} disabled={true} />
                  <div className="mt-2 text-system-white48 text-xs">{t('personal.phone.note')}</div>
                </div>
              )}
            </>
          )}

          {RenderDivider()}

          {RenderSectionTitle(t('personal.delete.label'))}

          <div className="mb-4 text-base text-system-white64">
            {t.rich('personal.delete.description', {
              bold: (chunks) => <span className="font-bold">{chunks}</span>,
              br: () => <br />,
            })}
          </div>
          <Button onClick={() => setDialogOpen(true)} variant="danger">
            {t('personal.delete.button')}
          </Button>
        </div>
        <DeleteUserDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSuccess={() => {
            onClose();
            router.push(HOME_PAGE);
          }}
        />
      </DialogContent>
    </Dialog>
  );
};
